<template>
  <div v-show="show">
    <BaseRadio
      name="payment_type"
      v-if="variant === 'radio'"
      v-model="selected"
      class="f-flex f-items-center"
      :class="getMethodSpecificClassName"
      @input="paymentMethodChanged"
    >
      <div class="f-flex f--mt-0.5">
        <div class="f-mr-4">{{ label }}</div>
        <img
          class="f-h-5 f-max-w-min"
          v-if="renderImage"
          :src="paymentMethod.image_url"
          :alt="paymentMethod.name"
        />
        <span class="f-text-sm f-text-gray-400" v-if="price !== null">
          {{ formattedPrice(price) }}
        </span>
      </div>
    </BaseRadio>
    <div
      role="button"
      tabindex="0"
      class="f-p-5 f-h-20 f-rounded-md f-grid f-place-content-center hover:f-shadow-lg f-transition f-duration-300 f-cursor-pointer f-text-center f-text-gray-700 f-bg-white"
      :class="[
        active
          ? 'f-border-2 f-shadow-lg payment-method-selected'
          : 'f-border f-border-gray-300',
        getMethodSpecificClassName,
      ]"
      v-if="variant === 'tile'"
      @click="paymentMethodChanged"
      @keyup.enter="paymentMethodChanged"
      :aria-label="'Vybrat platební metodu: ' + label"
    >
      <img
        v-if="renderImage"
        :style="
          paymentMethod.image_url.includes('.svg')
            ? 'height: 2.5em; width: 8em;'
            : 'max-height: 4em'
        "
        class="f-max-w-full"
        :src="paymentMethod.image_url + '?v=2'"
        :alt="paymentMethod.name"
      />
      <template v-else>
        {{ label }}
        <span
          class="f-block f-w-full f-text-sm f-text-gray-400"
          v-if="price !== null"
        >
          {{ formattedPrice(price) }}
        </span>
      </template>
    </div>
  </div>
</template>

<script lang="ts">
import Vue, { PropType } from "vue";
import PaymentMethodData from "@/module/Form/Resources/PaymentMethodResource";
import FormResource from "@/module/Form/Resources/FormResource";
import {
  isStringOrNull,
  isObjectOrNull,
} from "@/module/ComponentPropsValidations/validations";
import money from "@/module/Filters/MoneyFilter";
import BaseRadio from "@/components/Form/Basic/Inputs/BaseRadio.vue";
import FormPaymentMethodItem from "@/module/FormPaymentMethodItem";

export default Vue.extend({
  name: "PaymentMethod",
  components: { BaseRadio },
  props: {
    paymentMethod: {
      type: Object as PropType<PaymentMethodData>,
      required: true,
    },
    formResource: {
      type: Object as PropType<FormResource>,
      required: true,
    },
    currency: {
      type: String,
      required: true,
    },
    country: {
      required: true,
      validator: isStringOrNull,
    },
    shippingCountry: {
      required: true,
      validator: isStringOrNull,
    },
    active: {
      type: Boolean,
      required: true,
    },
    index: {
      type: Number,
      required: true,
    },
    phoneRequiredOrFilled: {
      type: Boolean,
      required: true,
    },
    orderPriceIncVat: {
      type: Number,
      required: true,
    },
    singleColumn: {
      type: Boolean,
      required: true,
    },
    variant: {
      type: String as PropType<"tile" | "radio">,
      required: true,
    },
    paymentMethodItem: {
      type: null as unknown as PropType<FormPaymentMethodItem | null>,
      required: true,
      validator: isObjectOrNull,
    },
  },

  data(): { selected: boolean } {
    return {
      selected: false,
    };
  },

  computed: {
    renderImage(): boolean {
      return (
        this.paymentMethod.image_url !== null &&
        this.paymentMethod.payment_type !== "collect on delivery" &&
        this.paymentMethod.payment_type !== "cash" &&
        !(this.paymentMethod.payment_type === "wire" && this.bank === "wire")
      );
    },

    label(): string {
      const key = "payment_method.methods." + this.name + ".name";
      let label = this.$t(key).toString();

      if (label === key) {
        label = this.paymentMethod.name;
      }

      return label;
    },

    description(): string | null {
      const key = "payment_method.methods." + this.name + ".description";
      const label = this.$t(key).toString();

      if (label === key) {
        return null;
      }

      return label;
    },

    name(): string {
      let name: string = (this.paymentMethod.payment_type ||
        this.paymentMethod.instrument) as string;

      if (this.paymentMethod.bank !== null) {
        name += "." + this.paymentMethod.bank;
      }

      return name;
    },

    show(): boolean {
      if (this.name === "twisto" && !this.phoneRequiredOrFilled) {
        return false;
      }

      if (
        this.name === "apple pay" &&
        // @ts-ignore
        !(window.ApplePaySession && window.ApplePaySession.canMakePayments())
      ) {
        return false;
      }

      let minPrice = null;

      if (
        this.name.startsWith("wire.") ||
        [
          "credit card",
          "bitcoin",
          "paypal",
          "sms",
          "gpay",
          "apple pay",
          "klarna",
        ].includes(this.name)
      ) {
        minPrice = 0.001;
      }

      if (minPrice !== null && this.orderPriceIncVat < minPrice) {
        return false;
      }

      const allowedByCurrency =
        this.paymentMethod.currencies.includes("EVERY") ||
        this.paymentMethod.currencies.includes(this.currency);

      let country = this.country as string | null;

      if (
        this.paymentMethod.payment_type === "collect on delivery" &&
        this.paymentMethodItem
      ) {
        country = (country ?? this.shippingCountry) as string | null;

        if (country === null) {
          return false;
        }

        const allowedCountries = this.paymentMethodItem
          ? this.paymentMethodItem.getCountriesOptions()
          : this.paymentMethod.countries;

        const allowedByCountry =
          allowedCountries.includes("EVERY") ||
          allowedCountries.includes(country);

        return allowedByCurrency && allowedByCountry;
      }

      if (country === null) {
        return allowedByCurrency;
      }

      const allowedCountries = this.paymentMethod.countries;

      const allowedByCountry =
        allowedCountries.includes("EVERY") ||
        allowedCountries.includes(country);

      return allowedByCurrency && allowedByCountry;
    },

    bank(): string | null {
      if (this.paymentMethod.payment_type !== "wire") {
        return null;
      }

      return this.paymentMethod.bank || "wire";
    },

    price(): number | null {
      if (this.paymentMethodItem === null) {
        return null;
      }

      const country = (this.shippingCountry ?? this.country) as string | null;

      if (country === null) {
        return null;
      }

      const formItem =
        this.paymentMethodItem.findFormItemByCountryCode(country);

      if (formItem === null) {
        return null;
      }

      if (this.formResource.show_prices_including_vat) {
        return formItem.currentPriceIncVat;
      }

      return formItem.currentPrice;
    },

    getMethodSpecificClassName(): string {
      return "f-" + this.name.replace(/\s|\.|_/g, "-");
    },
  },

  methods: {
    paymentMethodChanged(): void {
      this.$emit("paymentMethodChanged", {
        index: this.index,
        payment_type:
          this.paymentMethod.payment_type || this.paymentMethod.instrument,
        bank: this.bank,
        payment_method_item: this.paymentMethodItem,
      });
    },

    formattedPrice(price: number): string {
      return money(price, this.$i18n);
    },
  },

  watch: {
    active() {
      this.selected = this.active;
    },

    show(show: boolean): void {
      if (show) {
        return;
      }

      if (!this.active) {
        return;
      }

      this.$emit("paymentMethodChanged", null);
    },
  },

  created(): void {
    if (!this.show) {
      return;
    }

    this.$emit("visiblePaymentMethod", {
      index: this.index,
      payment_type:
        this.paymentMethod.payment_type || this.paymentMethod.instrument,
      bank: this.bank,
      payment_method_item: this.paymentMethodItem,
    });
  },
});
</script>

<style lang="scss">
.fapi-order-form {
  .payment-method-selected {
    border-color: var(--input-color);
  }
}
</style>
